import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { Icon } from '@iconify/react'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import { useFeatureVersion } from '@/src/hooks/useFeatureVersion'
import { useToggleFeature } from '@/src/hooks/useToggleFeature'
import type { FeatureVersion } from '@/src/types/featureExperience'
import { SIDEBAR_NAVIGATION_PILOT_SHORT } from '@/src/components/featureExperience/sidebarNavigationPilotCopy'

interface FeatureToggleProps {
  featureKey: string
  label?: string
  description?: string
  restaurantId?: string | number | null

  /** When set, overrides `label` with copy that reflects the active version. */
  resolveLabel?: (version: FeatureVersion) => string

  /** When set, overrides `description` with copy that reflects the active version. */
  resolveDescription?: (version: FeatureVersion) => string | undefined

  /** `phase === 'pilot'` alert — defaults to sidebar navigation pilot copy. */
  pilotPhaseTitle?: string
  pilotPhaseDescription?: string

  /** Shown when `lockAfterOptInToNext` and user is on `next`. */
  optInLockCaption?: string
}

export const FeatureToggle: React.FC<FeatureToggleProps> = ({
  featureKey,
  label = 'Try new version',
  description,
  restaurantId,
  resolveLabel,
  resolveDescription,
  pilotPhaseTitle,
  pilotPhaseDescription,
  optInLockCaption
}) => {
  const { canToggle, lockAfterOptInToNext, cutoverReached, isInRolloutAudience, isForcedResolution, phase } =
    useFeatureVersion(featureKey)
  const { currentVersion, toggle, isLoading } = useToggleFeature(featureKey, { restaurantId })

  // Keep switch state + label/description consistent: `currentVersion` includes user preference when present.
  const displayLabel = resolveLabel ? resolveLabel(currentVersion) : label
  const displayDescription = resolveDescription ? resolveDescription(currentVersion) : description

  if (cutoverReached) {
    return null
  }

  if (!isInRolloutAudience) {
    return null
  }

  if (phase === 'pilot') {
    return (
      <Alert
        severity='info'
        variant='standard'
        icon={<Icon icon='mdi:information' width={22} height={22} />}
        sx={{
          mt: 0.5,
          py: 1,
          px: 1.25,
          alignItems: 'flex-start',
          bgcolor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          color: theme => theme.palette.info.main,
          '& .MuiAlert-icon': {
            alignSelf: 'flex-start',
            pt: 0.25,
            mr: 1,
            color: theme => theme.palette.info.main,
            opacity: 1,
            display: 'flex'
          },
          '& .MuiAlert-message': { width: '100%', padding: 0 }
        }}
      >
        <Typography variant='caption' sx={{ fontWeight: 700, display: 'block', mb: 0.35 }}>
          {pilotPhaseTitle ?? 'Navigation pilot'}
        </Typography>
        <Typography variant='caption' sx={{ display: 'block', lineHeight: 1.4, opacity: 0.95 }}>
          {pilotPhaseDescription ?? SIDEBAR_NAVIGATION_PILOT_SHORT}
        </Typography>
      </Alert>
    )
  }

  if (!canToggle || isForcedResolution) {
    return (
      <Box sx={{ opacity: 0.7 }}>
        <Typography variant='subtitle2'>{displayLabel}</Typography>
        {displayDescription && (
          <Typography variant='caption' color='text.secondary'>
            {displayDescription}
          </Typography>
        )}
        <Typography variant='caption' display='block' sx={{ mt: 1 }}>
          {isForcedResolution ? 'This experience is now the default.' : 'Switching is not available.'}
        </Typography>
      </Box>
    )
  }

  if (lockAfterOptInToNext && currentVersion === 'next') {
    return (
      <Box sx={{ opacity: 0.85 }}>
        <FormControlLabel
          control={<Switch checked disabled inputProps={{ 'aria-label': displayLabel }} />}
          label={
            <Box>
              <Typography variant='subtitle2'>{displayLabel}</Typography>
              {displayDescription && (
                <Typography variant='caption' color='text.secondary' display='block'>
                  {displayDescription}
                </Typography>
              )}
              <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
                {optInLockCaption ?? 'New navigation is locked for your account; you cannot switch back to classic.'}
              </Typography>
            </Box>
          }
        />
      </Box>
    )
  }

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={currentVersion === 'next'}
            onChange={() => void toggle()}
            disabled={isLoading}
            inputProps={{ 'aria-label': `${displayLabel}. Toggle to switch layout.` }}
          />
        }
        label={
          <Box>
            <Typography variant='subtitle2'>{displayLabel}</Typography>
            {displayDescription && (
              <Typography variant='caption' color='text.secondary' display='block'>
                {displayDescription}
              </Typography>
            )}
          </Box>
        }
      />
    </Box>
  )
}
